import { referentiel } from '@admin-pmvb/core'
import { useQueries, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  api,
  corps,
  type Entite,
  lire,
  type Personne,
  requeteMoi,
  requetes,
  type Tiers,
} from '../api'
import { EspacesPrives } from '../composants/EspacesPrives'
import { Icone, type NomIcone } from '../composants/Icone'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { Vide } from '../composants/Section'
import { EntetePage } from '../composants/Ui'
import { OngletsReferentiel } from '../composants/OngletsReferentiel'
import {
  initiales,
  LIBELLES_REGIME_IMPOT,
  LIBELLES_REGIME_TVA,
  LIBELLES_TYPE_ENTITE,
  options,
  LIBELLES_ESPACE_DRIVE,
  LIBELLES_MODE_TRANSMISSION,
} from '../libelles'

/**
 * Aide du Drive partagé propre : à qui donner accès et avec quel rôle (ce que vérifie le
 * serveur avant d'accepter l'id), et ce qui se passe sans lui.
 */
function aideDrivePropre(privee: boolean, robot: string | null): string {
  const acces = robot
    ? `Ajouter d’abord le robot ${robot} comme membre « Gestionnaire de contenu » du Drive partagé.`
    : 'Ajouter d’abord le robot (son adresse : auprès d’un administrateur, le Drive n’étant pas configuré ici) comme membre « Gestionnaire de contenu » du Drive partagé.'
  return [
    'Id du Drive (dans son adresse).',
    acces,
    'L’id est vérifié avant d’être accepté.',
    privee
      ? 'Sans lui, rien ne se range : le rangement attend (jamais dans un Drive commun).'
      : 'L’emporte sur le Drive partagé ci-dessus.',
  ].join(' ')
}

/**
 * Champs de la fiche d'une entité. Un espace privé (`privee`) ne range que dans son Drive
 * propre (D3) : pas de choix du Drive commun.
 */
export function champsEntite(
  tiers: Tiers[],
  options_: { privee?: boolean; compteServiceDrive?: string | null } = {},
): Champ[] {
  const privee = options_.privee ?? false
  const champs: Champ[] = [
    { nom: 'nom', libelle: 'Nom', type: 'texte', requis: true },
    {
      nom: 'type',
      libelle: 'Type',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_TYPE_ENTITE),
    },
    { nom: 'forme', libelle: 'Forme', type: 'texte', aide: 'SAS, SASU, SC, SARL…' },
    { nom: 'siren', libelle: 'SIREN', type: 'texte' },
    {
      nom: 'regimeTva',
      libelle: 'Régime de TVA',
      type: 'choix',
      options: options(LIBELLES_REGIME_TVA),
    },
    {
      nom: 'regimeImpot',
      libelle: 'Régime d’imposition',
      type: 'choix',
      options: options(LIBELLES_REGIME_IMPOT),
    },
    {
      nom: 'cloture',
      libelle: 'Clôture de l’exercice',
      type: 'texte',
      aide: 'Format MM-JJ, par exemple 12-31',
    },
    {
      nom: 'cabinetId',
      libelle: 'Cabinet comptable',
      type: 'choix',
      options: tiers
        .filter((t) => t.role === 'cabinet')
        .map((t) => ({ valeur: t.id, libelle: t.nom })),
    },
    { nom: 'plateformeAgreee', libelle: 'Plateforme agréée', type: 'texte' },
    {
      nom: 'transmission',
      libelle: 'Justificatifs au cabinet',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_MODE_TRANSMISSION),
    },
    {
      nom: 'adresseCollecte',
      libelle: 'Adresse de collecte du cabinet',
      type: 'email',
      aide: 'Envoi par mail seulement : adresse de collecte des justificatifs du dossier Tiime. Sans objet en dépôt direct (rien ne part par mail).',
    },
    {
      nom: 'espaceDrive',
      libelle: 'Drive partagé',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_ESPACE_DRIVE),
      aide: 'Sans objet pour un espace privé : il ne range que dans son Drive propre.',
    },
    {
      nom: 'drivePartageId',
      libelle: 'Drive partagé propre à l’entité',
      type: 'texte',
      aide: aideDrivePropre(privee, options_.compteServiceDrive ?? null),
    },
    {
      nom: 'racineClassement',
      libelle: 'Drive : racine du plan de classement',
      type: 'texte',
      aide: 'Dossier qui doit déjà exister, depuis la racine du Drive partagé, par exemple « Classement/SC PMVB ». Dessous, l’appli range comme un cabinet : « 1 - Dossier permanent » (statuts, baux, prêts, contrats) et un dossier par exercice, découpé par cycle (achats, ventes, banque, fiscal, social, travaux, courriers, clôture). Renseignée, elle remplace les dossiers ci-dessous ; les pièces déjà rangées ne bougent pas.',
    },
    {
      nom: 'cheminAchats',
      libelle: 'Drive : dossier des achats',
      type: 'texte',
      aide: 'Depuis la racine du Drive partagé, par exemple « Compta/Achats/{AAAA}/{AAAAMM} ». Le début doit exister ; {AAAA}, {MM} et {AAAAMM} sont créés au besoin.',
    },
    {
      nom: 'cheminVentes',
      libelle: 'Drive : dossier des ventes',
      type: 'texte',
      aide: 'Vide : celui des autres pièces.',
    },
    {
      nom: 'cheminReleves',
      libelle: 'Drive : dossier des relevés',
      type: 'texte',
      aide: 'Vide : celui des autres pièces.',
    },
    {
      nom: 'cheminAutres',
      libelle: 'Drive : dossier des autres pièces',
      type: 'texte',
      aide: 'Avis, courriers, contrats. Aucun dossier renseigné : les pièces attendent dans l’appli.',
    },
    {
      nom: 'identifiants',
      libelle: 'Autres identifiants',
      type: 'liste',
      aide: 'Un par ligne : SIRET, TVA intracommunautaire, ancien nom, nom commercial… Ce qui la désigne sur les factures.',
    },
  ]
  return privee ? champs.filter((c) => c.nom !== 'espaceDrive') : champs
}

/**
 * Espace privé : seulement à la création, jamais à la modification (le caractère privé se fixe
 * une fois pour toutes). Titulaire : une personne humaine, non administratrice (le serveur le
 * refuse aussi), qui y reçoit le rôle d'associée ; l'administrateur qui crée l'espace n'y reçoit
 * aucun rôle.
 */
function champsEspacePrive(personnes: Personne[], administrateurs: ReadonlySet<string>): Champ[] {
  return [
    {
      nom: 'privee',
      libelle: 'Espace privé',
      type: 'booleen',
      aide: 'Visible de sa seule titulaire (administrateurs compris). Ne se retire plus ensuite.',
    },
    {
      nom: 'titulaireId',
      libelle: 'Titulaire de l’espace privé',
      type: 'choix',
      options: personnes
        .filter((p) => p.type === 'humain' && !administrateurs.has(p.id))
        .map((p) => ({ valeur: p.id, libelle: p.nom })),
      aide: 'Obligatoire si « Espace privé » est cochée, et seulement dans ce cas. Pas un administrateur.',
    },
  ]
}

/**
 * Formulaire de création d'une entité (administrateurs). Sans Drive partagé propre : celui d'un
 * espace privé se règle par sa titulaire, sur la fiche de l'espace (le serveur refuse l'id à la
 * création d'un espace privé) ; une entité commune le reçoit aussi sur sa fiche.
 */
export function champsCreationEntite(
  tiers: Tiers[],
  personnes: Personne[],
  administrateurs: ReadonlySet<string>,
  compteServiceDrive: string | null | undefined,
): Champ[] {
  return [
    ...champsEntite(tiers, { compteServiceDrive }).filter((c) => c.nom !== 'drivePartageId'),
    ...champsEspacePrive(personnes, administrateurs),
  ]
}

const ICONES_TYPE: Record<Entite['type'], NomIcone> = {
  societe: 'apartment',
  activite_nom_propre: 'storefront',
  foyer: 'family_home',
}

export function Entites() {
  const moi = useSuspenseQuery(requeteMoi).data
  const entites = useQuery(requetes.entites)
  const tiers = useQuery({ ...requetes.tiers, enabled: moi.administrateur })
  const personnes = useQuery({ ...requetes.personnes, enabled: moi.administrateur })
  const roles = useQuery(requetes.roles())
  const comptes = useQueries({
    queries: (entites.data ?? []).map((e) => requetes.comptes(e.id)),
  })
  const client = useQueryClient()
  const [ajout, setAjout] = useState(false)
  const [departIncomplet, setDepartIncomplet] = useState<string[]>([])

  return (
    <main>
      <EntetePage
        surtitre="Référentiel"
        titre="Entités"
        action={
          moi.administrateur
            ? { icone: 'add', libelle: 'Nouvelle entité', onClick: () => setAjout(true) }
            : undefined
        }
      />
      <OngletsReferentiel actif="entites" />
      <div className="pile">
        {ajout && (
          <section className="carte">
            <h2>Nouvelle entité</h2>
            <Formulaire
              champs={champsCreationEntite(
                tiers.data ?? [],
                personnes.data ?? [],
                new Set(
                  (roles.data ?? [])
                    .filter((role) => role.niveau === 'administrateur')
                    .map((role) => role.personneId),
                ),
                moi.compteServiceDrive,
              )}
              initial={{ transmission: 'aucune', espaceDrive: 'famille', privee: false }}
              libelleBouton="Créer l’entité"
              onAnnuler={() => setAjout(false)}
              onValider={async (v) => {
                const cree = await lire(
                  api.entites.$post({ json: corps(referentiel.entiteCreation, v) }),
                )
                // Un espace privé n'est plus visible ensuite : ce qui manque se dit maintenant.
                setDepartIncomplet(cree.avertissementsDepart)
                await client.invalidateQueries()
                setAjout(false)
              }}
            />
          </section>
        )}
        {departIncomplet.length > 0 && (
          <div className="alerte" role="status">
            Espace privé créé, départ incomplet (sa fiche le dit à sa titulaire, à prévenir) :{' '}
            {departIncomplet.join(' ')}{' '}
            <button type="button" className="bouton-discret" onClick={() => setDepartIncomplet([])}>
              Fermer
            </button>
          </div>
        )}
        {entites.isPending && <Vide>Chargement…</Vide>}
        <div className="grille-entites">
          {entites.data?.map((e, i) => {
            const cabinet = tiers.data?.find((t) => t.id === e.cabinetId)?.nom
            const banques = [...new Set((comptes[i]?.data ?? []).map((c) => c.banque))]
            const regime = [
              e.regimeTva ? LIBELLES_REGIME_TVA[e.regimeTva] : null,
              e.regimeImpot ? LIBELLES_REGIME_IMPOT[e.regimeImpot] : null,
            ]
              .filter(Boolean)
              .join(' · ')
            const responsables = (roles.data ?? []).filter(
              (r) => r.entiteId === e.id && r.responsable,
            )
            return (
              <Link
                key={e.id}
                to="/entites/$id"
                params={{ id: e.id }}
                className={`carte carte-lien carte-entite ${e.archivee ? 'archivee' : ''}`}
              >
                <div className="carte-entite-haut">
                  <span className="pastille-type">
                    <Icone nom={e.archivee ? 'inventory_2' : ICONES_TYPE[e.type]} taille={22} />
                  </span>
                  <div className="carte-entite-nom">
                    <strong>{e.nom}</strong>
                    <span>
                      {[e.forme, LIBELLES_TYPE_ENTITE[e.type]].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  {e.archivee && (
                    <span className="badge grand">
                      <Icone nom="inventory_2" taille={13} />
                      Archivée
                    </span>
                  )}
                </div>
                <dl className="details">
                  {moi.administrateur && (
                    <>
                      <dt>
                        <Icone nom="calculate" />
                        Cabinet
                      </dt>
                      <dd>{cabinet ?? 'Aucun'}</dd>
                    </>
                  )}
                  <dt>
                    <Icone nom="account_balance" />
                    Banque
                  </dt>
                  <dd>{banques.length > 0 ? banques.join(', ') : 'Aucun compte'}</dd>
                  <dt>
                    <Icone nom="percent" />
                    Régime
                  </dt>
                  <dd>{regime || 'Non renseigné'}</dd>
                </dl>
                <div className="carte-entite-pied">
                  <span className="discret">Responsables</span>
                  <span className="avatars">
                    {responsables.map((r) => (
                      <span key={r.id} className="avatar" title={r.personne}>
                        {initiales(r.personne).slice(0, 3)}
                      </span>
                    ))}
                  </span>
                  <Icone nom="arrow_forward" />
                </div>
              </Link>
            )
          })}
        </div>
        {moi.administrateur && <EspacesPrives />}
      </div>
    </main>
  )
}
